// src/pages/DebtStudio/components/Issues/index.jsx

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useProfile } from "context/ProfileContext";
import { useAppAccess } from "context/AppAccessContext";
import { supabase } from "services/supabaseClient";
import { useDialog } from "context/DialogContext";

import {
  useDebtStudio,
  validateDealForm,
  DEFAULT_FORM,
  buildOrUpdateStructure,
} from "pages/DebtStudio/context/DebtStudioContext";

import Page from "components/Page";
import FileRibbon from "components/FileManager/FileRibbon";
import FileManagerModal from "components/FileManager/FileManagerModal";
import FileManagerPanel from "components/FileManager/FileManagerPanel";
import TopTabMenu from "components/TopTabMenu";

import DealForm from "./DealForm";
import ScaleBuilder from "./ScaleBuilder";
import DealStructure from "./DealStructure";
import Reports from "./Reports";
import IssueDocuments from "./Documents";

import { useNavigationGuard } from "hooks/useNavigationGuard";
import { useIssuesFileManager, issuesOpenView } from "./fileManager";

/* ------------------------------------------------------------------
 * Helpers
 * ------------------------------------------------------------------ */

function safeJson(value, fallback) {
  if (value == null) return fallback;
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return fallback;
    }
  }
  return value;
}

function normalizeAdditionalSources(value) {
  const parsed = safeJson(value, []);
  return Array.isArray(parsed) ? parsed : [];
}

function normalizeRefundedIssues(value) {
  const parsed = safeJson(value, []);
  return Array.isArray(parsed) ? parsed : [];
}

/* ------------------------------------------------------------------
 * User-facing field labels (Main tab)
 * ------------------------------------------------------------------ */

const DEAL_FIELD_LABELS = {
  dealName: "Issue Name",
  issuerId: "Issuer",
  issuerNameRaw: "Issuer",
  dealType: "Deal Type",
  interestMode: "Interest Mode",
  taxStatus: "Tax Status",

  parAmount: "Principal Amount",
  denomination: "Denomination",
  couponRate: "Coupon Rate",
  dayCount: "Day Count",
  couponFrequency: "Coupon Frequency",
  principalFrequency: "Principal Frequency",

  datedDate: "Dated Date",
  deliveryDate: "Delivery Date",
  firstCouponDate: "First Coupon Date",
  firstPrincipalDate: "First Principal Date",
  finalMaturityDate: "Final Maturity Date",
  firstCallDate: "First Call Date",
  fiscalYearEnd: "Fiscal Year End",
};

const FIELD_TO_SUBTAB = {
  parAmount: "Deal Info",
  denomination: "Deal Info",
  couponRate: "Deal Info",
  dayCount: "Deal Info",
  couponFrequency: "Deal Info",
  principalFrequency: "Deal Info",

  datedDate: "Dates & Terms",
  deliveryDate: "Dates & Terms",
  firstCouponDate: "Dates & Terms",
  firstPrincipalDate: "Dates & Terms",
  finalMaturityDate: "Dates & Terms",
  firstCallDate: "Dates & Terms",
  fiscalYearEnd: "Dates & Terms",
};

const toLabel = (key) => DEAL_FIELD_LABELS[key] || key;
const formatList = (keys = []) => keys.map(toLabel).join(", ");

const pickSubTabFromFields = (fields = []) => {
  for (const f of fields) {
    if (FIELD_TO_SUBTAB[f]) return FIELD_TO_SUBTAB[f];
  }
  return "Deal Info";
};

export default function DebtStudio() {
  const {
    profile,
    loading: profileLoading,
    availableOrgs,
    selectedOrg,
    orgMode,
    selectOrg,
  } = useProfile();

  const { apps, loading: appsLoading } = useAppAccess();
  const appList = Array.isArray(apps) ? apps : [];
  const hasAccess = !!(profile?.is_admin || appList.includes("debt-studio"));

  const {
    hasActiveDeal,
    form,
    currentOwner,
    resetAll,
    loadDeal,
    saveDeal,
    revertToLastSaved,
    ensureStructure,
    isDirty,
    setIsDirty,
    structureRows,
    scale,
    canEdit,
    canDelete,
    isLocked,
    structureNeedsRebuild,
    applyFirstCallDate,
    rebuildStructureFromTerms,
    adminLibraryEditEnabled,
    setAdminLibraryEditEnabled,
  } = useDebtStudio();

  const dsFM = useIssuesFileManager({ limit: 100 });

  const [activeTab, setActiveTab] = useState("Main");
  const [restoredReady, setRestoredReady] = useState(false);
  const [showOpenModal, setShowOpenModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [isCreatingNew, setIsCreatingNew] = useState(false);

  const { confirm, alert, showDialog } = useDialog();

  /* ------------------------------------------------------------------
   * Deal class helpers (still useful for UI/policy messages)
   * ------------------------------------------------------------------ */

  const dealClass = String(form?.dealClass || "org").toLowerCase();
  const isLibrary = dealClass === "library";
  const isPlatformAdmin = !!profile?.is_admin;

  useNavigationGuard(
    isDirty && !isLocked,
    saveDeal,
    revertToLastSaved,
    "You have unsaved changes in this deal. Save before leaving?"
  );

  /* ------------------------------------------------------------------
   * Top tabs (ScaleBuilder disabled when locked)
   * ------------------------------------------------------------------ */

  const topTabs = useMemo(() => {
    const base = [
      { key: "Main", label: "Main" },
      { key: "Structure", label: "Structure" },
      { key: "Reports", label: "Reports" },
    ];

    const isLoan =
      String(form?.financingType || "").toLowerCase() === "loan";

    if (!isLocked && !isLoan) {
      base.splice(1, 0, {
        key: "ScaleBuilder",
        label: "ScaleBuilder",
      });
    }

    if (form?.id) {
      base.push({ key: "Documents", label: "Documents" });
    }

    return base;
  }, [isLocked, form?.financingType, form?.id]);

  /* ------------------------------------------------------------------
   * Restore helpers
   * ------------------------------------------------------------------ */

  useEffect(() => {
    window.setDebtStudioTab = (tabName = "Main") => {
      setActiveTab(tabName);
      window.scrollTo({ top: 0, behavior: "smooth" });
    };
    return () => delete window.setDebtStudioTab;
  }, []);

  useEffect(() => {
    const hasRestoredSession = !!sessionStorage.getItem("debtstudio.session");
    const wasInactive = !!localStorage.getItem("compass.inactivityLogout");

    if (!restoredReady && hasRestoredSession && wasInactive) {
      const hasWorkingDeal = !!(form?.id || String(form?.dealName || "").trim());
      if (selectedOrg && hasWorkingDeal) {
        localStorage.removeItem("compass.inactivityLogout");
        setIsDirty(false);
        setRestoredReady(true);
        setActiveTab("Main");
      }
    }
  }, [restoredReady, selectedOrg, form, setIsDirty]);

  /* ------------------------------------------------------------------
   * Workspace reset helper
   * ------------------------------------------------------------------ */

  const returnToInitialState = useCallback(() => {
    resetAll();

    setRestoredReady(false);
    setIsCreatingNew(false);

    setActiveTab("Main");
    setShowOpenModal(false);

    sessionStorage.removeItem("debtstudio.session");
    localStorage.removeItem("compass.inactivityLogout");

    sessionStorage.setItem("dealFormSubTab", "Deal Info");
  }, [resetAll]);

  /* ------------------------------------------------------------------
   * Tab guard
   * ------------------------------------------------------------------ */

  const handleTopTabChange = useCallback(
    async (nextTab) => {
      if (nextTab === activeTab) return;

      const leavingMain = activeTab === "Main" && nextTab !== "Main";
      if (!leavingMain) {
        setActiveTab(nextTab);
        return;
      }

      if (isLocked) {
        setActiveTab(nextTab);
        return;
      }

      const { ok, missing, invalid } = validateDealForm(form);
      if (!ok) {
        const fields = [...(missing || []), ...(invalid || [])];
        const subTab = pickSubTabFromFields(fields);
        sessionStorage.setItem("dealFormSubTab", subTab);

        const missingLabels = formatList(missing);
        const invalidLabels = formatList(invalid);

        await alert(
          "Cannot proceed. Please complete the required fields first:" +
            (missingLabels ? `\n\nMissing: ${missingLabels}` : "") +
            (invalidLabels ? `\n\nInvalid: ${invalidLabels}` : ""),
          { title: "Required fields missing", okLabel: "OK" }
        );

        setActiveTab("Main");
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }

      const hasOkStructure = ensureStructure();
      if (!hasOkStructure) {
        await alert(
          "Unable to build the initial structure from the current terms. Please verify Dates & Terms.",
          { title: "Structure error", okLabel: "OK" }
        );
        setActiveTab("Main");
        return;
      }

      try {
        applyFirstCallDate?.();
      } catch (e) {
        console.warn("⚠️ [DebtStudio] applyFirstCallDate failed", e);
      }

      if (structureNeedsRebuild) {
        const ok2 = await confirm(
          "Dates & terms changed in a way that affects maturities and schedules. Build/re-build the structure now?",
          {
            title: "Build structure?",
            okLabel: "Build",
            cancelLabel: "Cancel",
          }
        );

        if (!ok2) {
          setActiveTab("Main");
          return;
        }

        const rebuilt = rebuildStructureFromTerms?.();
        if (!rebuilt) {
          await alert("Could not rebuild structure. Please verify Dates & Terms.", {
            title: "Build failed",
            okLabel: "OK",
          });
          setActiveTab("Main");
          return;
        }
      }

      setActiveTab(nextTab);
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    [
      confirm,
      alert,
      activeTab,
      isLocked,
      form,
      ensureStructure,
      applyFirstCallDate,
      structureNeedsRebuild,
      rebuildStructureFromTerms,
    ]
  );

  /* ------------------------------------------------------------------
   * Handlers
   * ------------------------------------------------------------------ */

  const handleNew = useCallback(() => {
    if (!selectedOrg) {
      void alert("Select an organization first.", { title: "No organization" });
      return;
    }

    setIsCreatingNew(true);
    resetAll();

    const newForm = {
      ...DEFAULT_FORM,
      additionalSources: [],
    };

    loadDeal({
      form: newForm,
      structureRows: [],
      scale: null,
      solution: null,
      selectedOrg,
    });

    sessionStorage.setItem("dealFormSubTab", "Deal Info");
    window.setDebtStudioTab?.("Main");
  }, [selectedOrg, resetAll, loadDeal, alert]);

  const handleSave = useCallback(
    async () => {
      if (isLocked) return;

      const { ok, missing } = validateDealForm(form);
      if (!ok) {
        await alert(
          "Please complete all required fields before saving:\n" + formatList(missing),
          { title: "Required fields missing", okLabel: "OK" }
        );
        return;
      }

      if (!structureRows?.length) {
        const skeleton = buildOrUpdateStructure(form);
        if (skeleton.length) {
          loadDeal({
            form,
            structureRows: skeleton,
            scale: null,
            solution: null,
            selectedOrg,
          });
        }
      }

      setIsSaving(true);
      try {
        const result = await saveDeal();
        if (result?.ok) dsFM.refresh?.();
        return result;
      } finally {
        setIsSaving(false);
      }
    },
    [alert, isLocked, form, structureRows, loadDeal, saveDeal, dsFM, selectedOrg]
  );

  const handleSelectOrg = useCallback(
    async (org) => {
      if (String(org?.id || "") === String(selectedOrg?.id || "")) return;

      if (!isDirty) {
        selectOrg(org);
        return;
      }

      const buttons = canEdit
        ? [
            { value: "cancel", label: "Cancel", variant: "ghost" },
            { value: "discard", label: "Discard and switch", variant: "danger" },
            { value: "save", label: "Save and switch", variant: "primary" },
          ]
        : [
            { value: "cancel", label: "Cancel", variant: "ghost" },
            { value: "discard", label: "Discard and switch", variant: "danger" },
          ];

      const result = await showDialog({
        title: "Switch organization?",
        message: "This issue has unsaved changes. Save or discard them before switching organizations.",
        buttons,
        defaultAction: "cancel",
      });

      if (result?.value === "save") {
        const saved = await handleSave();
        if (!saved?.ok) return;
      } else if (result?.value !== "discard") {
        return;
      }

      selectOrg(org);
    },
    [canEdit, handleSave, isDirty, selectedOrg?.id, selectOrg, showDialog]
  );

  const handleSaveAs = useCallback(
    async () => {
      // Save As is allowed even when current deal is locked/library/read-only
      if (!selectedOrg || !profile?.id) return;

      const defaultName = `${form.dealName} (Copy)`;
      const dialogResult = await showDialog({
        title: "Save As",
        message: "Save issue as:",
        input: {
          label: "Issue Name",
          defaultValue: defaultName,
          autoFocus: true,
        },
        checkbox: isPlatformAdmin
          ? {
              label: "Save as Library Issue",
              helpText:
                "Creates a shared, locked library copy and publishes it to organizations entitled to this issuer.",
            }
          : null,
        buttons: [
          { value: "cancel", label: "Cancel", variant: "ghost" },
          { value: "ok", label: "Save", variant: "primary" },
        ],
        defaultAction: "ok",
      });

      if (dialogResult?.value !== "ok") return;

      const newName = String(dialogResult?.inputValue || "").trim();
      if (!newName) return;

      const saveAsLibrary =
        isPlatformAdmin && dialogResult?.checkboxChecked === true;

      if (saveAsLibrary && !form?.issuerId) {
        await alert(
          "A canonical issuer is required before an issue can be saved to the library.",
          { title: "Issuer required", okLabel: "OK" }
        );
        return;
      }

      const { ok, missing } = validateDealForm(form);
      if (!ok) {
        await alert(
          "Please complete all required fields before saving:\n" +
            formatList(missing),
          { title: "Required fields missing", okLabel: "OK" }
        );
        return;
      }

      const clonedForm = {
        ...form,
        id: null,
        dealName: newName,
        created_by: profile.id,
        sharing: saveAsLibrary ? "org-view" : "user",

        dealClass: saveAsLibrary ? "library" : "org",
      };

      const rowsToSave = structureRows?.length
        ? structureRows
        : buildOrUpdateStructure(clonedForm);

      const res = await saveDeal({
        formOverride: clonedForm,
        structureRowsOverride: rowsToSave,
        scaleOverride: scale,
        selectedOrgOverride: selectedOrg,
        forceInsert: true,
      });

      if (!res?.ok || !res?.id) {
        await alert("Save As failed. Copy was not saved.", {
          title: "Save As failed",
          okLabel: "OK",
        });
        return;
      }

      if (saveAsLibrary) {
        const { error: publishError } = await supabase.rpc(
          "ds_publish_library_deal_access",
          { p_deal_id: res.id }
        );

        if (publishError) {
          await alert(
            "The library issue was saved, but organization access could not be published. " +
              publishError.message,
            { title: "Library publish warning", okLabel: "OK" }
          );
        }
      }

      loadDeal({
        form: { ...clonedForm, id: res.id, created_by: profile.id },
        structureRows: rowsToSave,
        scale,
        solution: null,
        selectedOrg,
      });

      dsFM.refresh?.();
    },
    [
      showDialog,
      alert,
      selectedOrg,
      profile?.id,
      form,
      structureRows,
      scale,
      saveDeal,
      loadDeal,
      dsFM,
      isPlatformAdmin,
    ]
  );

  const handleDelete = useCallback(
    async () => {
      if (!form?.id || !canDelete) return;

      const deletingLibraryIssue = isLibrary && isPlatformAdmin;
      const message = deletingLibraryIssue
        ? "Delete this shared library issue? It will be removed for every organization and cannot be undone."
        : "Delete this issue? This cannot be undone.";

      const ok = await confirm(message, {
        title: deletingLibraryIssue ? "Delete library issue" : "Delete issue",
        okLabel: "Delete",
        cancelLabel: "Cancel",
      });
      if (!ok) return;

      try {
        await dsFM.deleteRecord?.(form.id);
        returnToInitialState();
        await alert("Issue deleted.", { title: "Deleted", okLabel: "OK" });
      } catch (err) {
        await alert(err?.message || "Failed to delete issue.", {
          title: "Delete failed",
          okLabel: "OK",
        });
      }
    },
    [
      confirm,
      alert,
      canDelete,
      form?.id,
      dsFM,
      returnToInitialState,
      isLibrary,
      isPlatformAdmin,
    ]
  );

  /* ------------------------------------------------------------------
   * Load helpers
   * ------------------------------------------------------------------ */

  const handleLoad = useCallback(
    async (deal) => {
      try {
        const isLibraryDeal = String(deal?.deal_class || "").toLowerCase() === "library";
        if (
          !isLibraryDeal &&
          String(deal?.org_id || "") !== String(selectedOrg?.id || "")
        ) {
          await alert("This issue does not belong to the selected organization.", {
            title: "Organization mismatch",
            okLabel: "OK",
          });
          return;
        }

        const parsedStructure = safeJson(deal?.structure, []);
        const parsedScale = safeJson(deal?.scale, null);
        const parsedCOI = safeJson(deal?.cost_of_issuance, []);
        const parsedAdditionalSources = normalizeAdditionalSources(deal?.additional_sources);
        const parsedRefundedIssues = normalizeRefundedIssues(deal?.refunded_issues);
        const parsedWrapConfig = safeJson(deal?.wrap_config, null);
        const parsedWrapManualCashflows = safeJson(deal?.wrap_manual_cashflows, []);
        const parsedWrapDsIssueRefs = safeJson(deal?.wrap_ds_issue_refs, []);

        const loadedForm = {
          ...DEFAULT_FORM,
          id: deal.id,
          created_by: deal.created_by || null,
          issuerId: deal.issuer_id || null,
          issuerNameRaw: deal.issuer_id
            ? deal?.issuers?.legal_name || ""
            : deal.issuer_name_raw || "",
          issuerStatus: deal.issuer_status || (deal.issuer_id ? "canonical" : "custom"),
          dealName: deal.name,
          financingType: deal.financing_type || DEFAULT_FORM.financingType,
          dealType: deal.deal_type,
          dealClass: deal.deal_class || "org",
          orgId: deal.org_id ?? null,
          lienPriority: deal.lien_priority ?? DEFAULT_FORM.lienPriority,
          interestMode: deal.interest_mode,
          taxStatus: deal.tax_status,
          sharing: deal.sharing,
          status: deal.status,
          datedDate: deal.dated_date,
          deliveryDate: deal.delivery_date,
          firstCouponDate: deal.first_coupon_date,
          firstPrincipalDate: deal.first_principal_date,
          finalMaturityDate: deal.final_maturity_date,
          firstCallDate: deal.first_call_date || "",
          fiscalYearEnd: deal.fiscal_year_end || "",
          parAmount: deal.par_amount,
          denomination: deal.denomination,
          couponRate: deal.coupon_rate,
          dayCount: deal.day_count,
          couponFrequency: deal.coupon_frequency,
          principalFrequency: deal.principal_frequency,
          capiEnabled: !!deal.capi_enabled,
          capiDate: deal.capi_date || "",
          capiInvestmentRate: deal.capi_investment_rate ?? 0,
          additionalSources: parsedAdditionalSources,
          refunded_issues: parsedRefundedIssues,
          wrap_config: parsedWrapConfig,
          wrap_manual_cashflows: parsedWrapManualCashflows,
          wrap_ds_issue_refs: parsedWrapDsIssueRefs,
          pricingMode: deal.pricing_mode,
          scale: parsedScale,
          costOfIssuance: Array.isArray(parsedCOI) ? parsedCOI : [],
          structureMode: deal.structure_mode || "level_payment",
          amortizationPeriods: deal.amortization_periods ?? null,
          loanTermPeriods: deal.loan_term_periods ?? null,
          targetType: deal.target_type || "par_amount",
          targetValue: deal.target_value ?? null,
          targetGross: deal.target_gross ?? null,
          fundingType: deal.funding_type || "gross",
        };

        loadDeal({
          form: loadedForm,
          structureRows: parsedStructure,
          scale: parsedScale,
          solution: null,
          selectedOrg,
        });

        setIsCreatingNew(false);

        dsFM.openRecord?.(deal);
        setActiveTab("Main");
      } catch (err) {
        console.error("Error loading deal:", err);
      }
    },
    [alert, loadDeal, selectedOrg, dsFM]
  );

  const loadDealById = useCallback(
    async (dealId) => {
      if (!dealId || !selectedOrg) return;

      const { data, error } = await supabase
        .from("ds_deals")
        .select(
          `
          *,
          issuers:issuer_id (
            legal_name
          )
        `
        )
        .eq("id", dealId)
        .maybeSingle();

      if (error) {
        await alert(`Unable to load issue. ${error.message}`, {
          title: "Load failed",
          okLabel: "OK",
        });
        return;
      }

      if (!data) {
        await alert("Unable to load issue.", {
          title: "Load failed",
          okLabel: "OK",
        });
        return;
      }

      const isLibraryDeal = String(data.deal_class || "").toLowerCase() === "library";
      if (
        !isLibraryDeal &&
        String(data.org_id || "") !== String(selectedOrg.id)
      ) {
        await alert("This issue does not belong to the selected organization.", {
          title: "Organization mismatch",
          okLabel: "OK",
        });
        return;
      }

      handleLoad(data);
    },
    [selectedOrg, handleLoad, alert]
  );

  if (profileLoading || appsLoading) {
    return <div>Loading user profile…</div>;
  }

  if (!hasAccess) {
    return (
      <Page title="DebtStudio - Issues">
        <div className="p-6 text-muted">You do not have access to this app.</div>
      </Page>
    );
  }

  const showWorkspace = !!(restoredReady || hasActiveDeal || isCreatingNew);

  return (
    <Page
      title="DebtStudio - Issues"
      showOrganizationSelector
      onSelectOrganization={handleSelectOrg}
      ribbon={
        <FileRibbon
          entityName="Issue"
          fileManager={{
            availableOrgs,
            selectedOrg,
            orgMode,
            selectOrg: handleSelectOrg,
          }}
          onNew={handleNew}
          onSave={canEdit && showWorkspace && isDirty ? handleSave : null}
          // Save As should be available whenever a deal is loaded (including locked/library/read-only)
          onSaveAs={showWorkspace ? handleSaveAs : null}
          onOpenBrowse={showWorkspace ? () => setShowOpenModal(true) : null}
          onDelete={canDelete ? handleDelete : null}
          currentIssuer={form?.issuerNameRaw}
          currentName={form?.dealName}
          currentOwner={isLibrary ? null : currentOwner}
          animateSaving
          currentStatus={
            isSaving
              ? "saving"
              : !form?.id
              ? "unsaved"
              : isLocked
              ? "read-only"
              : isDirty
              ? "modified"
              : "saved"
          }
          isReadOnly={isLocked}
          isLibrary={isLibrary && isLocked}
        >
          {!showWorkspace ? (
            !selectedOrg ? (
              <div className="text-sm text-muted">
                Select an organization above to browse issues.
              </div>
            ) : (
              <FileManagerPanel
                entityName="Issue"
                adapter={dsFM}
                view={issuesOpenView}
                onSelect={(deal) => loadDealById(deal.id)}
                showToolbar
                showFooter
                maxHeightClassName="max-h-[55vh]"
              />
            )
          ) : null}
        </FileRibbon>
      }
    >
      {showWorkspace && (
        <div className="rounded-2xl border border-border bg-app shadow-sm">
          {isLibrary && isPlatformAdmin ? (
            <div className="border-b border-border px-6 py-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <label className="flex items-center gap-2 text-sm text-app">
                  <input
                    type="checkbox"
                    checked={!!adminLibraryEditEnabled}
                    onChange={(e) =>
                      setAdminLibraryEditEnabled?.(e.target.checked)
                    }
                  />
                  <span>Edit locked library issue</span>
                </label>

                {adminLibraryEditEnabled ? (
                  <div className="text-xs font-medium text-red-600">
                    Admin library edit mode is enabled. Changes will modify the
                    shared library issue.
                  </div>
                ) : (
                  <div className="text-xs text-muted">
                    Library issue is locked. Enable edit mode to modify it as a
                    platform admin.
                  </div>
                )}
              </div>
            </div>
          ) : null}

          <TopTabMenu tabs={topTabs} activeTab={activeTab} onChange={handleTopTabChange} />
          <div className="p-6 border-t border-border rounded-b-2xl">
            {activeTab === "Main" && <DealForm isLocked={isLocked} />}
            {activeTab === "ScaleBuilder" && <ScaleBuilder isLocked={isLocked} />}
            {activeTab === "Structure" && <DealStructure isLocked={isLocked} />}
            {activeTab === "Reports" && <Reports isLocked={isLocked} />}
            {activeTab === "Documents" && (
              <IssueDocuments
                deal={form}
                canUpload={isLibrary ? isPlatformAdmin : canEdit}
              />
            )}
          </div>
        </div>
      )}

      {showOpenModal && selectedOrg ? (
        <FileManagerModal
          entityName="Issue"
          adapter={dsFM}
          view={issuesOpenView}
          onClose={() => setShowOpenModal(false)}
          onSelect={(deal) => {
            loadDealById(deal.id);
            setShowOpenModal(false);
          }}
        />
      ) : null}
    </Page>
  );
}
